import { useCallback, useEffect, useRef, useState } from "react";
import { startLearningLlmStreamV2 } from "../services/learningGraphqlSseClientV2";
import type {
  AttachmentInput,
  LlmStreamContentBlock,
  LlmStreamMessageCompletedEvent,
} from "@axio/graphql/generated/graphql";

// V2 variant of `useLearningLlmStream`. Handles the richer delta events introduced for v2
// (MCQ/subjective question blocks, tool start/result events, message-completed events that
// carry blocks + metadata) and streams through the v2 SSE client (no service-side pacing).

type StreamCompletionEvent = {
  chatId?: string;
  messageId?: string;
  blocks?: LlmStreamContentBlock[];
  metadata?: Record<string, unknown>;
};

interface StartStreamOptions {
  chatId?: string;
  workspaceId?: string;
  attachment?: AttachmentInput;
  contentNodeId?: string;
  inlineResponseChoiceIndex?: number;
  visualizationFocus?: string;
  skipChatLookup?: boolean;
  onChunk?: (payload: { text: string; messageId?: string; isFinalChunk?: boolean }) => void;
  onChatId?: (chatId: string) => void;
  onComplete?: (payload: { event?: StreamCompletionEvent; text: string }) => void;
  onObjectiveCompleted?: (payload: { objectiveIds: string[] }) => void;
  onAllObjectivesCompleted?: () => void;
  onError?: (payload: { error: Error }) => void;
  onToolStart?: (payload: { tool: string; jobId?: string | null }) => void;
  onToolResult?: (payload: { tool: string; result: Record<string, unknown> }) => void;
  onMcqBlock?: (payload: { questionId?: string | null; question: string; choices: string[] }) => void;
  onSubjectiveBlock?: (payload: { questionId?: string | null; question: string }) => void;
}

export function useLearningLlmStreamV2() {
  const controllerRef = useRef<AbortController | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const startStream = useCallback(async (question: string, options: StartStreamOptions = {}) => {
    const {
      onComplete,
      onError,
      onChunk,
      onChatId,
      onObjectiveCompleted,
      onAllObjectivesCompleted,
      onToolStart,
      onToolResult,
      onMcqBlock,
      onSubjectiveBlock,
      ...streamOptions
    } = options;

    if (!streamOptions.contentNodeId) {
      throw new Error("Missing contentNodeId for learning LLM stream.");
    }

    controllerRef.current?.abort();

    const controller = new AbortController();
    controllerRef.current = controller;

    setIsStreaming(true);

    let accumulatedText = "";
    let completionEvent: StreamCompletionEvent | undefined;

    try {
      await startLearningLlmStreamV2(
        question,
        controller,
        {
          onChunk: (event) => {
            const ev = event as Record<string, unknown>;
            if (ev.__typename === "LLMStreamMessageDeltaEvent") {
              if (ev.chatId) {
                onChatId?.(ev.chatId as string);
              }
              const delta = ev.delta as
                | {
                    __typename?: string;
                    type?: string;
                    text?: string;
                    questionId?: string;
                    question?: string;
                    choices?: string[];
                  }
                | undefined;
              if (delta?.__typename === "LLMStreamTextBlock") {
                const text = delta.text ?? "";
                if (text) {
                  accumulatedText += text;
                }
                onChunk?.({
                  text,
                  messageId: ev.messageId as string | undefined,
                  isFinalChunk: (ev.isFinalChunk as boolean | undefined) ?? false,
                });
                if (ev.isFinalChunk) {
                  setIsStreaming(false);
                }
              }
              // Use the domain `type` field (BlockType enum) to identify assessment blocks
              if (delta?.type === "mcq_question") {
                onMcqBlock?.({
                  questionId: delta.questionId ?? null,
                  question: delta.question ?? "",
                  choices: delta.choices ?? [],
                });
              }
              if (delta?.type === "subjective_question") {
                onSubjectiveBlock?.({
                  questionId: delta.questionId ?? null,
                  question: delta.question ?? "",
                });
              }
            }

            if (ev.__typename === "LLMStreamMessageCompletedEvent") {
              const completedEvent = ev as unknown as LlmStreamMessageCompletedEvent;
              const msg = completedEvent.message;
              if (msg.chatId) {
                onChatId?.(msg.chatId);
              }
              completionEvent = {
                chatId: msg.chatId,
                messageId: msg.id,
                blocks: msg.blocks ?? [],
                metadata: (msg.metadata as Record<string, unknown> | undefined) ?? undefined,
              };
              setIsStreaming(false);
              onComplete?.({ event: completionEvent, text: accumulatedText });
            }

            if (ev.__typename === "LLMStreamToolStartEvent") {
              onToolStart?.({
                tool: (ev as Record<string, unknown>).tool as string,
                jobId: ((ev as Record<string, unknown>).jobId as string | null | undefined) ?? null,
              });
            }

            if (ev.__typename === "LLMStreamToolResultEvent") {
              onToolResult?.({
                tool: (ev as Record<string, unknown>).tool as string,
                result: (ev as Record<string, unknown>).result as Record<string, unknown>,
              });
            }

            if (ev.__typename === "LLMStreamArtifactCreatedEvent") {
              const artifact = ev.artifact as
                | {
                    type?: string;
                    data?: { isCompleted?: boolean; objectiveId?: string };
                  }
                | undefined;
              if (
                artifact?.type === "objective.progress.changed" &&
                artifact?.data?.isCompleted === true &&
                typeof artifact?.data?.objectiveId === "string"
              ) {
                onObjectiveCompleted?.({ objectiveIds: [artifact.data.objectiveId] });
              }

              if (artifact?.type === "objectives.completed.all") {
                onAllObjectivesCompleted?.();
              }
            }

            if (ev.__typename === "LLMStreamErrorEvent") {
              const message = (ev.errorMessage as string) || "LLM stream error";
              setIsStreaming(false);
              onError?.({ error: new Error(message) });
            }
          },
          onError: (error) => {
            setIsStreaming(false);
            onError?.({ error });
          },
          onComplete: () => {
            setIsStreaming(false);
          },
        },
        streamOptions
      );
    } catch (err) {
      if ((err as DOMException)?.name === "AbortError") return;
      setIsStreaming(false);
      throw err;
    }
  }, []);

  const cancelStream = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setIsStreaming(false);
  }, []);

  return { startStream, cancelStream, isStreaming };
}

/** Use this type where `startStream` is passed as a prop (avoid `typeof` on type-only hook imports). */
export type LearningLlmStartStreamV2 = ReturnType<typeof useLearningLlmStreamV2>["startStream"];

export default useLearningLlmStreamV2;
